import i18next, { type TFunction } from 'i18next';
import type {
  DisruptionCause,
  DisruptionPaxBreakdown,
  DisruptionSeverity,
  DisruptionStaleness,
  DisruptionStalenessState,
  DisruptionStatus,
  RosterPriorityTag,
  RosterRebookingStatus,
} from '@/lib/disruptions/types';

// Friendly labels + tones for the disruption enums (frontend-conventions §18 —
// human labels, not raw enums).
//
// i18n: this module is not a component, so it holds translation KEYS, never
// language (the `tower-i18n.ts` precedent). Every label helper resolves its key
// through a translator: a component passes the `t` from `useTranslation(…)`,
// whose identity changes on `languageChanged` — that is what makes a memoised
// list redraw in the new language — and every other caller (tests, event
// handlers, pure model modules) falls back to `disruptionsT`, which is bound to
// the namespace but never to a language, so it always resolves against whatever
// the console is showing right now.
//
// The key tables hold FULLY-QUALIFIED keys (`disruptions:…`) on purpose: these
// enums are rendered from half a dozen namespaces (pnr, roster, tower, cases,
// caseWork, rebooking), and a `t` bound to any of them resolves a qualified key
// unchanged.

/** A translator for this module's keys — any namespace's `t` is assignable. */
export type DisruptionsT = TFunction<'disruptions'>;

/** Resolves a `disruptions` key against the console's live language. */
export const disruptionsT: DisruptionsT = i18next.getFixedT(null, 'disruptions');

const STATUS_KEY: Record<DisruptionStatus, string> = {
  DETECTED: 'disruptions:enums.status.DETECTED',
  CONTAINED: 'disruptions:enums.status.CONTAINED',
  RESOLVED: 'disruptions:enums.status.RESOLVED',
  MERGED: 'disruptions:enums.status.MERGED',
};

/** Human caption for a disruption's lifecycle status. */
export function statusLabel(status: DisruptionStatus, t: DisruptionsT = disruptionsT): string {
  return t(STATUS_KEY[status]);
}

const CAUSE_KEY: Record<DisruptionCause, string> = {
  WEATHER: 'disruptions:enums.cause.WEATHER',
  MECHANICAL: 'disruptions:enums.cause.MECHANICAL',
  STRIKE: 'disruptions:enums.cause.STRIKE',
  ATC: 'disruptions:enums.cause.ATC',
  SECURITY: 'disruptions:enums.cause.SECURITY',
  IT_OUTAGE: 'disruptions:enums.cause.IT_OUTAGE',
  OTHER: 'disruptions:enums.cause.OTHER',
};

/**
 * ENGLISH cause captions — the pre-i18n table, kept for the one call site
 * outside this route that has not migrated (`routes/imports/commit-bar.tsx`,
 * whose surface ships its own trilingual bundle).
 *
 * @deprecated render `causeLabel(cause, t)` instead.
 */
export const CAUSE_LABEL: Record<DisruptionCause, string> = {
  WEATHER: 'Weather',
  MECHANICAL: 'Mechanical',
  STRIKE: 'Strike',
  ATC: 'ATC',
  SECURITY: 'Security',
  IT_OUTAGE: 'IT Outage',
  OTHER: 'Other',
};

/** Human caption for a disruption's root cause. */
export function causeLabel(cause: DisruptionCause, t: DisruptionsT = disruptionsT): string {
  return t(CAUSE_KEY[cause]);
}

/** Tailwind classes per status (badge tone). */
export const STATUS_TONE: Record<DisruptionStatus, string> = {
  DETECTED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  CONTAINED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  RESOLVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  MERGED: 'bg-muted text-muted-foreground',
};

// ── Staleness / SLA badge (disruption.tds.md §3.3) ──────────────────────────

/** Short chip key per staleness state — what the operator scans for. `OK` has no
 *  chip of its own, so it falls back to the bare "Stale" caption. */
const STALENESS_KEY: Record<DisruptionStalenessState, string> = {
  STALE_UNWORKED: 'disruptions:enums.staleness.STALE_UNWORKED',
  STALE_NO_CASES: 'disruptions:enums.staleness.STALE_NO_CASES',
  STALE_STALLED: 'disruptions:enums.staleness.STALE_STALLED',
  WINDOW_EXPIRED: 'disruptions:enums.staleness.WINDOW_EXPIRED',
  CLOSABLE: 'disruptions:enums.staleness.CLOSABLE',
  PREDICTED_EXPIRED: 'disruptions:enums.staleness.PREDICTED_EXPIRED',
  OK: 'disruptions:enums.staleness.fallback',
};

/** Short chip caption per staleness state. */
export function stalenessLabel(
  state: DisruptionStalenessState,
  t: DisruptionsT = disruptionsT,
): string {
  return t(STALENESS_KEY[state]);
}

/** Badge tone by severity (critical/warning loud, info muted). */
export const STALENESS_TONE: Record<DisruptionStaleness['severity'], string> = {
  critical: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  info: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  none: 'bg-muted text-muted-foreground',
};

/** A one-line chip caption: label + age, e.g. "Stale · unworked · 26h". */
export function stalenessChip(s: DisruptionStaleness, t: DisruptionsT = disruptionsT): string {
  return t('disruptions:enums.staleness.chip', {
    label: stalenessLabel(s.state, t),
    hours: s.ageHours,
  });
}

/** Friendly airport list from scope URNs (`urn:nexa:airport:SCL` → `SCL`). */
export function airportCodes(airports: readonly string[]): string {
  return airports
    .map((a) => a.split(':').pop() ?? a)
    .filter(Boolean)
    .join(', ');
}

/**
 * Friendly operator label from a `urn:user:<sub>` URN (S16 / NEX-292) — URNs are
 * secondary, human labels primary (frontend-conventions §18). The bulk lock 409 +
 * progress poll carry only the operator URN (no displayName from the domain), so
 * we derive a readable tail: `urn:user:auth0|alice` → "alice". Falls back to
 * "another operator" (the shared `common` copy) when the URN is
 * empty/unparseable.
 */
export function operatorLabel(
  operatorUrn: string | null | undefined,
  t: DisruptionsT = disruptionsT,
): string {
  const unknown = (): string => t('common:holds.lock.anotherOperator');
  if (!operatorUrn) return unknown();
  // The auth0 provider prefix (`auth0|`, `google-oauth2|`, …) isn't human — drop it.
  const tail = operatorUrn.split(':').pop() ?? operatorUrn;
  const name = tail.includes('|') ? (tail.split('|').pop() ?? tail) : tail;
  return name.trim() || unknown();
}

// ── Situation-board severity (ranked server-side, disruption-overview.ranking) ─

const SEVERITY_KEY: Record<DisruptionSeverity, string> = {
  critical: 'disruptions:enums.severity.critical',
  warning: 'disruptions:enums.severity.warning',
  active: 'disruptions:enums.severity.active',
  settled: 'disruptions:enums.severity.settled',
};

/** Queue-card caption per board tier. */
export function severityLabel(
  severity: DisruptionSeverity,
  t: DisruptionsT = disruptionsT,
): string {
  return t(SEVERITY_KEY[severity]);
}

/** Left rail / marker background per tier (the board's one color axis). */
export const SEVERITY_BG: Record<DisruptionSeverity, string> = {
  critical: 'bg-rose-500',
  warning: 'bg-amber-500',
  active: 'bg-sky-500',
  settled: 'bg-slate-300 dark:bg-slate-600',
};

/** Marker/legend text color per tier (SVG uses currentColor). */
export const SEVERITY_TEXT: Record<DisruptionSeverity, string> = {
  critical: 'text-rose-500',
  warning: 'text-amber-500',
  active: 'text-sky-500',
  settled: 'text-slate-400 dark:text-slate-500',
};

/** Relative age, coarse on purpose ("2h ago" beats a timestamp in a scan). */
export function timeAgo(iso: string, t: DisruptionsT = disruptionsT): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return t('disruptions:timeAgo.justNow');
  if (min < 60) return t('disruptions:timeAgo.minutes', { count: min });
  const hours = Math.floor(min / 60);
  if (hours < 48) return t('disruptions:timeAgo.hours', { count: hours });
  return t('disruptions:timeAgo.days', { count: Math.floor(hours / 24) });
}

/**
 * `4_820_000` minor + `USD` → `$48,200` — an amount the airline PAYS (`airline-money@v2`,
 * commercial-contract M3: the BFF converts Nexa's cost to the contract price before it
 * leaves the server). `null` / `undefined` → «No disponible» / "Not available": the
 * price card was unreadable, or the amount cannot be priced — never shown as 0.
 */
export function formatPriceMinor(
  minor: number | null | undefined,
  currency: string,
  t: DisruptionsT = disruptionsT,
): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) {
    return t('disruptions:price.unavailable');
  }
  return formatMoneyMinor(minor, currency);
}

/**
 * `1_200_000` minor + `USD` → `$12,000` for an amount that is NOT Nexa's (shown as is):
 * entitlements, caps. Airline-facing amounts of Nexa's services — and a rebooking option's
 * `adcMinor`, which is `null` without a price card when Nexa buys the flight
 * (commercial-contract D17/D20) — use {@link formatPriceMinor}.
 */
export function formatMoneyMinor(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(0)} ${currency}`;
  }
}

/** True when an airline amount is known (a number) — false renders «No disponible». */
export function isPriced(minor: number | null | undefined): minor is number {
  return typeof minor === 'number' && Number.isFinite(minor);
}

/**
 * `239433` + `USD` → `$239,433`. Format a cost in MAJOR currency units (e.g. whole
 * USD) with the locale-appropriate currency symbol and thousand separators.
 * Rounds to the nearest whole unit (no decimals) for consistency with `formatPriceMinor`.
 *
 * This fixes the ambiguity where `toLocaleString()` without currency formatting
 * shows `239.433` in Spanish locales — which looks like 3 decimal places but is
 * actually the thousands separator (issue #616).
 */
export function formatCostMajor(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount).toLocaleString()} ${currency}`;
  }
}

/* ── S7 control-center roster labels ─────────────────────────────────────────── */

/** Friendly priority-chip keys (the raw tag drives the `priority` query param). */
const PRIORITY_KEY: Record<RosterPriorityTag, string> = {
  UMNR: 'disruptions:enums.priority.UMNR',
  VIP: 'disruptions:enums.priority.VIP',
  WCHR: 'disruptions:enums.priority.WCHR',
  MEDA: 'disruptions:enums.priority.MEDA',
  BUSINESS: 'disruptions:enums.priority.BUSINESS',
  FIRST: 'disruptions:enums.priority.FIRST',
  ONWARD_AT_RISK: 'disruptions:enums.priority.ONWARD_AT_RISK',
};

/** Friendly caption for a priority tag; an unknown tag renders as itself. */
export function priorityLabel(tag: string, t: DisruptionsT = disruptionsT): string {
  const key = PRIORITY_KEY[tag as RosterPriorityTag];
  return key ? t(key) : tag;
}

/**
 * Short chip keys for a priority tag (the column badges). UMNR / VIP / WCHR /
 * MEDA are IATA SSR CODES — the operator scans for them and they read the same
 * in every language, so their copy IS the code. The last three are prose
 * (cabin + onward-connection), so they translate: leaving them English is what
 * made a Spanish roster show "First" next to a tooltip reading "Primera".
 */
const PRIORITY_SHORT_KEY: Record<RosterPriorityTag, string> = {
  UMNR: 'disruptions:enums.priorityShort.UMNR',
  VIP: 'disruptions:enums.priorityShort.VIP',
  WCHR: 'disruptions:enums.priorityShort.WCHR',
  MEDA: 'disruptions:enums.priorityShort.MEDA',
  BUSINESS: 'disruptions:enums.priorityShort.BUSINESS',
  FIRST: 'disruptions:enums.priorityShort.FIRST',
  ONWARD_AT_RISK: 'disruptions:enums.priorityShort.ONWARD_AT_RISK',
};

/** Short chip caption for a priority tag; an unknown tag renders as itself. */
export function priorityShortLabel(tag: string, t: DisruptionsT = disruptionsT): string {
  const key = PRIORITY_SHORT_KEY[tag as RosterPriorityTag];
  return key ? t(key) : tag;
}

/** Rebooking-status keys for the roster filter + column. */
const REBOOKING_STATUS_KEY: Record<RosterRebookingStatus, string> = {
  NOT_APPLICABLE: 'disruptions:enums.rebookingStatus.NOT_APPLICABLE',
  PENDING: 'disruptions:enums.rebookingStatus.PENDING',
  OPTIONS_READY: 'disruptions:enums.rebookingStatus.OPTIONS_READY',
  // SELECTED ≠ ticketed with the airline — it means a flight option was chosen +
  // a seat is held (the domain has no airline-issuance state; the carrier tickets
  // it). "Flight assigned" reads truer to the operator than the engine's "Selected".
  SELECTED: 'disruptions:enums.rebookingStatus.SELECTED',
  NO_OPTIONS: 'disruptions:enums.rebookingStatus.NO_OPTIONS',
  OVERRIDDEN: 'disruptions:enums.rebookingStatus.OVERRIDDEN',
  DEFERRED: 'disruptions:enums.rebookingStatus.DEFERRED',
};

/** Friendly rebooking-status caption; an unknown status renders as itself. */
export function rebookingStatusLabel(status: string, t: DisruptionsT = disruptionsT): string {
  const key = REBOOKING_STATUS_KEY[status as RosterRebookingStatus];
  return key ? t(key) : status;
}

/**
 * Human copy for the coded `noOptionsReason` that explains WHY a PNR group's
 * rebookingStatus is NO_OPTIONS / NOT_APPLICABLE (FROZEN codes). POLICY_DISABLED is
 * the primary case (rebooking off by policy) and pairs with a "View policy" link to
 * the group's `governingPolicyUrn`. Codes are open-ended on the wire — an absent or
 * unrecognised code resolves to `undefined` so callers keep their generic copy.
 */
const NO_OPTIONS_REASON_KEY: Record<string, string> = {
  POLICY_DISABLED: 'disruptions:enums.noOptions.POLICY_DISABLED',
  NO_INVENTORY: 'disruptions:enums.noOptions.NO_INVENTORY',
  FILTERED_OUT: 'disruptions:enums.noOptions.FILTERED_OUT',
  NO_ROUTE: 'disruptions:enums.noOptions.NO_ROUTE',
  REJECTED_ALL: 'disruptions:enums.noOptions.REJECTED_ALL',
  HOLD_EXPIRED: 'disruptions:enums.noOptions.HOLD_EXPIRED',
  VENDOR_UNAVAILABLE: 'disruptions:enums.noOptions.VENDOR_UNAVAILABLE',
  OPTIONS_DEPARTED: 'disruptions:enums.noOptions.OPTIONS_DEPARTED',
};

/** Human "why" for a no-options code; `undefined` when absent/unrecognised. */
export function noOptionsReasonLabel(
  reason: string | null | undefined,
  t: DisruptionsT = disruptionsT,
): string | undefined {
  const key = reason ? NO_OPTIONS_REASON_KEY[reason] : undefined;
  return key ? t(key) : undefined;
}

/**
 * The copy key a surface uses for WHY a PNR has no flight option: `noOptionsBy.<CODE>`
 * for a recognised code, else the bare `noOptions`. Every list says the cause and the
 * way out — never a bare «no hay vuelos»: NO_ROUTE means Nexa never searched at all.
 */
export function noOptionsWhyKey(reason: string | null | undefined): string {
  return reason && NO_OPTIONS_REASON_KEY[reason] ? `noOptionsBy.${reason}` : 'noOptions';
}

/** Short keys for the no-options reason breakdown tiles (bulk progress dialog). */
const NO_OPTIONS_REASON_SHORT_KEY: Record<string, string> = {
  POLICY_DISABLED: 'disruptions:enums.noOptionsShort.POLICY_DISABLED',
  NO_INVENTORY: 'disruptions:enums.noOptionsShort.NO_INVENTORY',
  FILTERED_OUT: 'disruptions:enums.noOptionsShort.FILTERED_OUT',
  NO_ROUTE: 'disruptions:enums.noOptionsShort.NO_ROUTE',
  REJECTED_ALL: 'disruptions:enums.noOptionsShort.REJECTED_ALL',
  HOLD_EXPIRED: 'disruptions:enums.noOptionsShort.HOLD_EXPIRED',
  VENDOR_UNAVAILABLE: 'disruptions:enums.noOptionsShort.VENDOR_UNAVAILABLE',
  OPTIONS_DEPARTED: 'disruptions:enums.noOptionsShort.OPTIONS_DEPARTED',
};

/** Compact "why" for a no-options code; `undefined` when absent/unrecognised. */
export function noOptionsReasonShort(
  reason: string | null | undefined,
  t: DisruptionsT = disruptionsT,
): string | undefined {
  const key = reason ? NO_OPTIONS_REASON_SHORT_KEY[reason] : undefined;
  return key ? t(key) : undefined;
}

/** Friendly names for the third-party providers (vendor health + the
 *  roster "delayed by service Z" banner). Falls back to the raw key. */
const VENDOR_KEY: Record<string, string> = {
  inventory: 'disruptions:enums.vendor.inventory',
  airline: 'disruptions:enums.vendor.airline',
  risk: 'disruptions:enums.vendor.risk',
  hotel: 'disruptions:enums.vendor.hotel',
  wallet: 'disruptions:enums.vendor.wallet',
};

/** Friendly provider name; an unknown vendor renders as its raw key. */
export function vendorLabel(vendor: string, t: DisruptionsT = disruptionsT): string {
  const key = VENDOR_KEY[vendor];
  return key ? t(key) : vendor;
}

/** "flight inventory" / "flight inventory and airline" / "3 services". */
export function vendorListLabel(vendors: string[], t: DisruptionsT = disruptionsT): string {
  const names = vendors.map((v) => vendorLabel(v, t));
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) {
    // Spanish turns the conjunction "y" into "e" before an /i/ sound
    // ("aerolínea E inventario de vuelos"); English has no such rule, so both
    // keys carry the same copy there. The `hie-` diphthong keeps "y".
    const iSound = /^h?i(?!e)/i.test(names[1]!);
    return t(iSound ? 'disruptions:vendors.pairI' : 'disruptions:vendors.pair', {
      a: names[0],
      b: names[1],
    });
  }
  return t('disruptions:vendors.count', { count: names.length });
}

/** Badge variant per rebooking status (white-label tokens — no hardcoded brand). */
export const REBOOKING_STATUS_TONE: Record<
  RosterRebookingStatus,
  'muted' | 'info' | 'success' | 'warning' | 'error' | 'neutral'
> = {
  NOT_APPLICABLE: 'muted',
  PENDING: 'warning',
  OPTIONS_READY: 'info',
  SELECTED: 'success',
  NO_OPTIONS: 'error',
  OVERRIDDEN: 'neutral',
  DEFERRED: 'muted',
};

/**
 * PNR pax-type breakdown → a compact chip string, e.g. `{adt:2,chd:1,inf:1}` →
 * `"2A 1C 1I"`. Zero buckets are dropped; an all-zero breakdown yields `""`.
 */
export function formatPaxBreakdown(b: DisruptionPaxBreakdown): string {
  const parts: string[] = [];
  if (b.adt > 0) parts.push(`${b.adt}A`);
  if (b.chd > 0) parts.push(`${b.chd}C`);
  if (b.inf > 0) parts.push(`${b.inf}I`);
  return parts.join(' ');
}

/** `4` + `{adt:2,chd:1,inf:1}` → `"4 pax · 2A 1C 1I"` (breakdown dropped when empty). */
export function formatPaxSummary(paxCount: number, b: DisruptionPaxBreakdown): string {
  const noun = `${paxCount} pax`;
  const breakdown = formatPaxBreakdown(b);
  return breakdown ? `${noun} · ${breakdown}` : noun;
}

/** `2026-05-20T18:42:00Z` → `20 May 2026, 18:42`. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
